/**
 * Utilidades de formateo para la aplicación
 */

/**
 * Formatea un número como precio en formato argentino
 * Ejemplo: 10300.5 -> "$10.300,50"
 *
 * @param value - Número o string a formatear
 * @param showCents - Si mostrar centavos (default: true)
 * @returns Precio formateado con signo peso
 */
export function formatPrice(value: number | string | null | undefined, showCents: boolean = true): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) {
    return '-';
  }

  // Formato argentino: punto para miles, coma para decimales
  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  };

  const formatted = num.toLocaleString('es-AR', options);
  return `$${formatted}`;
}

/**
 * Parsea un string en formato argentino ("1.234,56" o "1234,56" o "1234.56" o "1234")
 * y devuelve un número. Si está vacío o inválido devuelve null.
 *
 * Heurística: si hay coma, ESA es la decimal y los puntos son miles.
 * Si solo hay puntos sin coma, asumimos que son separadores de miles
 * (formato AR habitual cuando el usuario escribe sin decimales).
 */
export function parseARNumber(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  // Quitar el simbolo $ y espacios
  const cleaned = trimmed.replace(/[$\s]/g, '');

  let normalized: string;
  if (cleaned.includes(',')) {
    // Hay coma → es la decimal AR. Puntos son miles.
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // Sin coma → asumimos puntos como miles (formato AR sin decimales)
    normalized = cleaned.replace(/\./g, '');
  }

  const num = parseFloat(normalized);
  return isNaN(num) ? null : num;
}

/**
 * Formatea un número con separadores AR (sin signo $)
 * Ejemplo: 1234.56 → "1.234,56"
 */
export function formatARNumber(value: number | null | undefined, decimals: number = 2): string {
  if (value === null || value === undefined || isNaN(value)) return '';
  return value.toLocaleString('es-AR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Normaliza un teléfono argentino al formato internacional que usa wa.me (sin + ni espacios).
 * Maneja los casos típicos de cómo escribe un cliente su número:
 *   "0381 15-579-1046"  -> "5493815791046"
 *   "381 5791046"       -> "5493815791046"
 *   "3815791046"        -> "5493815791046"
 *   "+54 9 381 5791046" -> "5493815791046"
 * Si ya viene con código de país lo respeta (asegurando el 9 de celular).
 */
export function toWhatsAppPhone(raw: string | null | undefined): string {
  let d = (raw || '').replace(/\D/g, '');
  if (!d) return '';

  // Ya trae código de país de Argentina
  if (d.startsWith('54')) {
    let rest = d.slice(2).replace(/^0/, '');
    // celulares: debe ir 9 después del 54
    if (!rest.startsWith('9')) rest = '9' + rest;
    return '54' + rest;
  }

  // Número nacional: quitar el 0 inicial de larga distancia
  d = d.replace(/^0/, '');
  // Quitar el "15" de celular si aparece tras el código de área (2 a 4 dígitos)
  d = d.replace(/^(\d{2,4})15(\d{6,8})$/, '$1$2');

  return '549' + d;
}

/**
 * Formatea un número como porcentaje
 * Ejemplo: 0.15 -> "15%"
 *
 * @param value - Número decimal (0.15 = 15%)
 * @param decimals - Cantidad de decimales (default: 0)
 */
export function formatPercent(value: number | null | undefined, decimals: number = 0): string {
  if (value === null || value === undefined) {
    return '-';
  }

  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Formatea una fecha en formato argentino
 * Ejemplo: "2024-01-15" -> "15/01/2024"
 *
 * IMPORTANTE: Las fechas en formato "YYYY-MM-DD" (solo fecha, sin hora) se parsean
 * como hora local para evitar problemas de timezone donde "2024-01-20" se mostraría
 * como "19/01/2024" al convertir de UTC a hora local.
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) {
    return '-';
  }

  let d: Date;

  if (typeof date === 'string') {
    // Si es formato "YYYY-MM-DD" (solo fecha, sin hora ni timezone)
    // parseamos manualmente para evitar problemas de timezone
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      d = new Date(year, month - 1, day); // month es 0-indexed
    } else {
      d = new Date(date);
    }
  } else {
    d = date;
  }

  if (isNaN(d.getTime())) {
    return '-';
  }

  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Formatea en vivo lo que se tipea en un input de dinero (miles con "." y decimal con ",").
 * Devolvé este valor como `value` del input y guardá el raw con parseCurrencyToRaw en onChange.
 */
export function formatCurrencyLive(raw: string | number | null | undefined): string {
  if (raw == null || raw === '') return '';
  const s0 = String(raw).replace(/[^\d.,]/g, '');
  let intPart = '';
  let decPart = '';
  if (s0.includes(',')) {
    const parts = s0.split(',');
    intPart = parts[0].replace(/\./g, '').replace(/\D/g, '');
    decPart = (parts[1] || '').replace(/\D/g, '').slice(0, 2);
  } else if ((s0.match(/\./g) || []).length === 1 && s0.indexOf('.') > 0 && /^\d+\.\d+$/.test(s0)) {
    const [a, b] = s0.split('.');
    intPart = a.replace(/\D/g, '');
    decPart = b.slice(0, 2);
  } else {
    intPart = s0.replace(/\D/g, '');
  }
  intPart = intPart.replace(/^0+(?=\d)/, '');
  if (intPart === '') intPart = '0';
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  // Preservar el separador decimal recién tipeado (coma o punto al final), para poder
  // seguir escribiendo los decimales (si no, se "comía" la coma y no dejaba poner decimales).
  const endsWithSep = /[.,]$/.test(String(raw));
  return decPart !== '' ? `${intFormatted},${decPart}` : (endsWithSep ? `${intFormatted},` : intFormatted);
}

/**
 * Convierte un valor formateado (o parcial) a string numérico con punto decimal (parseable).
 */
export function parseCurrencyToRaw(formatted: string): string {
  if (!formatted) return '';
  const cleaned = formatted.replace(/\./g, '').replace(',', '.');
  return cleaned.replace(/[^\d.]/g, '');
}

/**
 * Fecha corta argentina: "2027-08-05" -> "05-08-27" (dd-mm-yy)
 */
export function formatDateShort(date: string | null | undefined): string {
  if (!date) return '-';
  const m = String(date).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return formatDate(date);
  return `${m[3]}-${m[2]}-${m[1].slice(2)}`;
}

/**
 * Formatea fecha y hora
 * Ejemplo: "2024-01-15T10:30:00" -> "15/01/2024, 10:30"
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) {
    return '-';
  }

  const d = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(d.getTime())) {
    return '-';
  }

  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
